import React, { useEffect, useState } from 'react';
import { StatusDot } from '../../../components';
import { useTheme } from '../../../theme';
import { HERO, LINKS } from '../content';
import { HeroLines } from '../figures/HeroLines';
import { rich } from '../rich';

// The homepage hero's frame, type and buttons, with the decision lines in place of its
// wave lines. Where the homepage's map fades behind its words on smaller screens, these
// lines carry an answer, so below 1280px they stay at full strength, beside the words or
// under them (onehot.css).
export function OneHotHero() {
  const [isVisible, setIsVisible] = useState(false);
  const { resolvedTheme } = useTheme();
  const invert = resolvedTheme === 'dark';

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const fade = `opacity-0 ${isVisible ? 'animate-fade-up' : ''}`;
  const secondary = `text-xs sm:text-sm font-medium uppercase tracking-wide border ${
    invert ? 'border-white/30 text-white/70 hover:border-white hover:text-white hover:bg-white/10' : 'border-foreground/30 text-foreground/70 hover:border-foreground hover:text-foreground hover:bg-foreground/10'
  } px-4 sm:px-6 py-2.5 sm:py-3 rounded-full hover:shadow-md transition-all duration-300`;
  const primary = `text-xs sm:text-sm font-medium uppercase tracking-wide ${
    invert ? 'bg-white text-[hsl(220,25%,6%)] hover:bg-white/70' : 'bg-foreground text-background hover:bg-foreground/70'
  } px-4 sm:px-6 py-2.5 sm:py-3 rounded-full hover:shadow-lg transition-all duration-300`;

  return (
    <section className={`hero-section ${invert ? 'bg-[hsl(220,25%,6%)]' : ''} overflow-hidden`}>
      <div className="home-hero-frame oh-hero-frame mx-auto w-full hero-section-content">
        <div className="oh-hero-copy lg:min-h-[320px] max-w-full md:max-w-xl lg:max-w-2xl">
          <div className={`mb-4 sm:mb-6 ${fade}`} style={{ animationDelay: '0.1s' }}>
            <div className="flex items-center gap-2.5 sm:gap-3">
              <StatusDot pulse className="w-1 h-1 sm:w-1.5 sm:h-1.5" colorClass="bg-foreground/80" />
              <span className="text-eyebrow text-foreground-eyebrow">{HERO.eyebrow}</span>
            </div>
          </div>

          <h1 className={`text-hero-title text-balance ${invert ? 'text-white' : 'text-foreground'} ${fade}`} style={{ animationDelay: '0.2s' }}>
            {HERO.title}
          </h1>

          <p
            className={`mt-5 sm:mt-6 md:mt-8 text-hero-subtitle max-w-full sm:max-w-md md:max-w-xl ${invert ? 'text-white/70' : 'text-foreground-secondary'} ${fade}`}
            style={{ animationDelay: '0.35s' }}
          >
            {rich(HERO.line)}
          </p>

          <div className={`mt-8 sm:mt-10 flex flex-wrap gap-3 sm:gap-4 ${fade}`} style={{ animationDelay: '0.5s' }}>
            <a href={LINKS.playground} target="_blank" rel="noopener noreferrer" className={secondary}>Try the playground</a>
            <a href={LINKS.console} target="_blank" rel="noopener noreferrer" className={primary}>Get an API key</a>
          </div>
        </div>

        <div className="oh-hero-art" aria-hidden="true">
          <HeroLines />
        </div>
      </div>

      <div className={`absolute bottom-0 left-0 right-0 h-px ${invert ? 'bg-white/10' : 'bg-border'}`} />
    </section>
  );
}

export default OneHotHero;
